import React from "react";
import { BadgeCheck, ShieldCheck, Check, Award } from "lucide-react";

const CARD_IMG = "/assets/prima-cariera-card.webp";

// Sprint 1 simplification: 3 bullets max, single OUG info box, one trust badge
// under the headline. Aim is to reduce vertical real estate and the "legal /
// administrative" feel of the section without redesign.
const POINTS = [
  "Plata poate fi realizată folosind cardul Prima de carieră didactică.",
  "Programul este conceput pentru cadre didactice și include exemple practice aplicabile la clasă.",
  "Certificatul este emis în timp util pentru încărcarea în platforma aferentă programului.",
];

export default function PrimaCariera() {
  return (
    <section id="prima" className="scroll-mt-28 py-20 md:py-28 bg-[#EDEEF0]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* TEXT (left on desktop) */}
          <div className="lg:col-span-7 order-1">
            <span className="inline-flex items-center gap-2 rounded-full bg-white border border-[#E5E7EB] px-3 py-1 text-xs font-semibold text-[#965EA7] uppercase tracking-widest" data-testid="prima-badge">
              <BadgeCheck size={14} /> Eligibil pentru plata cu Prima de carieră didactică
            </span>
            <h2 className="mt-4 font-display text-3xl md:text-5xl font-bold text-[#373A45]">
              Poți achita acest curs folosind cardul <span className="text-[#965EA7]">Prima de carieră didactică</span>.
            </h2>

            {/* ECTS credit highlight — premium stat card. Sits immediately
                under the headline so it is the FIRST piece of value the
                visitor sees after the title. Built as a horizontal card
                with a large number (font-display), a soft purple gradient,
                a thick accent border on the left, and a discreet Award
                icon. Stays inline (no overlay) so the official card image
                on the right is never obstructed.

                Accessibility: the visible "1 credit ECTS" string is the
                accessible name; the icon is decorative (aria-hidden). */}
            <div
              className="mt-5 inline-flex max-w-md items-stretch overflow-hidden rounded-2xl border border-[#E5DBEA] bg-gradient-to-r from-white via-white to-[#F6EEFB] shadow-[0_4px_18px_-6px_rgba(150,94,167,0.25)]"
              data-testid="ects-credit-badge"
              data-analytics="ects-highlight"
              role="group"
              aria-label="Acest curs oferă 1 credit ECTS, recunoscut pentru dezvoltare profesională continuă"
            >
              <div
                className="flex items-center justify-center bg-gradient-to-br from-[#965EA7] to-[#7A4986] px-4 py-4 text-white"
                aria-hidden="true"
              >
                <Award size={26} strokeWidth={2.25} />
              </div>
              <div className="px-5 py-3.5">
                <div className="font-display text-2xl md:text-[1.65rem] font-bold leading-tight text-[#373A45]">
                  <span className="text-[#965EA7]">1</span> credit ECTS
                </div>
                <div className="mt-0.5 text-[12px] md:text-[13px] text-[#6B7280] leading-snug">
                  recunoscut pentru dezvoltare profesională continuă
                </div>
              </div>
            </div>

            {/* Subtle trust pill under the headline — replaces the louder
                payment-method badge that used to live at the bottom. */}
            <div
              className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#EAF4EE] border border-[#D8EBDE] px-3 py-1 text-xs font-semibold text-[#2F6B4E]"
              data-testid="prima-trust-pill"
            >
              <Check size={13} className="text-[#519872]" aria-hidden="true" />
              Potrivit pentru utilizarea Primei de carieră didactică
            </div>

            {/* Card image (mobile placement: between subheadline and bullets) */}
            <div className="lg:hidden mt-8">
              <CardImage />
            </div>

            <ul className="mt-8 space-y-4 text-[#373A45]">
              {POINTS.map((t, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`prima-point-${i}`}>
                  <span className="mt-1 h-6 w-6 rounded-full bg-[#519872] grid place-items-center text-white shrink-0">
                    <ShieldCheck size={14} />
                  </span>
                  <span className="leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>

            {/* OUG 39/2026 info box — single, compact reassurance card */}
            <div
              className="mt-7 rounded-2xl border border-[#D6E5F1] bg-[#F2F8FC] p-5 max-w-2xl"
              data-testid="prima-oug-box"
            >
              <div className="flex items-start gap-3">
                <span className="h-7 w-7 rounded-lg bg-[#3F8EBA] text-white grid place-items-center shrink-0">
                  <ShieldCheck size={14} />
                </span>
                <div>
                  <div className="font-display text-base font-semibold text-[#373A45]">
                    Program aliniat prevederilor OUG 39/2026
                  </div>
                  <p className="mt-1.5 text-sm text-[#6B7280] leading-relaxed">
                    Structura programului și documentele emise sunt pregătite pentru
                    participarea prin Prima de carieră didactică, conform cadrului
                    actual aplicabil.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* CARD IMAGE (right on desktop) */}
          <div className="hidden lg:block lg:col-span-5 order-2">
            <CardImage />
          </div>
        </div>

        {/* Micro-disclaimer */}
        <p className="mt-10 text-xs text-[#6B7280] max-w-3xl leading-relaxed" data-testid="prima-disclaimer">
          * Utilizarea cardului Prima de carieră didactică se face conform legislației
          și condițiilor aplicabile programului. Eligibilitatea achiziției și utilizarea
          fondurilor rămân responsabilitatea beneficiarului, conform reglementărilor în vigoare.
        </p>
      </div>
    </section>
  );
}

function CardImage() {
  return (
    <figure className="relative" data-testid="prima-card-figure">
      <img
        src={CARD_IMG}
        alt="Cardul oficial Prima de carieră didactică – Guvernul României, cofinanțat de Uniunea Europeană"
        loading="lazy"
        decoding="async"
        width="500"
        height="300"
        className="relative w-full h-auto"
      />
      <figcaption className="sr-only">
        Imaginea oficială a cardului Prima de carieră didactică, emis de Guvernul României
        prin Ministerul Investițiilor și Proiectelor Europene, cofinanțat de Uniunea Europeană.
      </figcaption>
    </figure>
  );
}
